import { apiClient } from "@/lib/api/client";
import type {
  DeviceCreateDto,
  DeviceDto,
  DeviceLocationSuggestionPageDto,
  DeviceAssignedUserSuggestionPageDto,
  DevicePageDto,
  PublicDeviceDto,
  DeviceServiceRecordCreateDto,
  DeviceServiceRecordDto,
  DeviceServiceRecordPlatformUserPageDto,
  DeviceServiceRecordUpdateDto,
  DeviceUpdateDto,
  MasterViewSnapshotPageDto,
} from "@/lib/api/types";

/** Aligned with FastAPI `GET /devices`: `limit` must be between 1 and 200 inclusive. */
const DEVICE_PAGE_MAX_LIMIT = 200;

export type ListDevicesPageParams = {
  tenantId?: string | null;
  workspaceScope?: "all" | null;
  limit?: number;
  offset?: number;
  search?: string;
  status?: string | null;
  statusScope?: "active" | "inactive" | null;
  protocol?: string | null;
  locationValue?: string | null;
  locationKind?: "site" | "city" | "state" | null;
  simType?: "phone" | "industrial" | "other" | null;
  assignmentState?: "assigned" | "unassigned" | null;
  assignedUserId?: string | null;
  assignedToMe?: boolean | null;
  warrantyScope?: "expiring_this_month" | "expired" | "next_30d" | "next_90d" | "unknown" | null;
  deviceCategoryId?: string | null;
  alertPreset?: "threshold_breached" | null;
  alertRangeKey?: "last_24h" | "last_7d" | "custom" | null;
  alertStart?: string | null;
  alertEnd?: string | null;
  /** Column sort key — see `device-table-columns.ts` / backend `_device_sort_key` */
  sort?: string | null;
  order?: "asc" | "desc" | null;
  moduleKey?: string | null;
};

export async function listDevicesPage(params: ListDevicesPageParams): Promise<DevicePageDto> {
  const {
    tenantId,
    workspaceScope,
    offset = 0,
    search,
    status,
    statusScope,
    protocol,
    locationValue,
    locationKind,
    simType,
    assignmentState,
    assignedUserId,
    assignedToMe,
    warrantyScope,
    deviceCategoryId,
    alertPreset,
    alertRangeKey,
    alertStart,
    alertEnd,
    sort,
    order,
    moduleKey,
  } = params;
  const rawLimit = params.limit ?? 50;
  const limit = Math.min(DEVICE_PAGE_MAX_LIMIT, Math.max(1, rawLimit));
  const query: Record<string, string | number> = {
    limit,
    offset,
  };
  if (tenantId) {
    query.tenant_id = tenantId;
  }
  if (workspaceScope === "all") {
    query.workspace_scope = "all";
  }
  if (search !== undefined && search !== "") {
    query.search = search;
  }
  if (status !== undefined && status !== null && status !== "") {
    query.status = status;
  }
  if (statusScope !== undefined && statusScope !== null) {
    query.status_scope = statusScope;
  }
  if (protocol !== undefined && protocol !== null && protocol !== "") {
    query.protocol = protocol;
  }
  if (locationValue !== undefined && locationValue !== null && locationValue !== "") {
    query.location_value = locationValue;
  }
  if (locationKind !== undefined && locationKind !== null) {
    query.location_kind = locationKind;
  }
  if (simType !== undefined && simType !== null) {
    query.sim_type = simType;
  }
  if (assignmentState !== undefined && assignmentState !== null) {
    query.assignment_state = assignmentState;
  }
  if (assignedUserId) {
    query.assigned_user_id = assignedUserId;
  }
  if (assignedToMe === true) {
    query.assigned_to_me = "true";
  }
  if (warrantyScope !== undefined && warrantyScope !== null) {
    query.warranty_scope = warrantyScope;
  }
  if (deviceCategoryId) {
    query.device_category_id = deviceCategoryId;
  }
  if (alertPreset !== undefined && alertPreset !== null) {
    query.alert_preset = alertPreset;
  }
  if (alertRangeKey !== undefined && alertRangeKey !== null) {
    query.alert_range_key = alertRangeKey;
  }
  if (alertStart !== undefined && alertStart !== null && alertStart !== "") {
    query.alert_start = alertStart;
  }
  if (alertEnd !== undefined && alertEnd !== null && alertEnd !== "") {
    query.alert_end = alertEnd;
  }
  if (sort !== undefined && sort !== null && sort !== "") {
    query.sort = sort;
  }
  if (order === "asc" || order === "desc") {
    query.order = order;
  }
  if (moduleKey) {
    query.module_key = moduleKey;
  }
  const { data } = await apiClient.get<DevicePageDto>("/devices", {
    params: query,
    __skipTenantHeader: workspaceScope === "all",
  });
  return data;
}

/** @deprecated Prefer listDevicesPage for large tenants. */
export async function getDevices(
  tenantId: string | null,
  workspaceScope?: "all" | null,
  moduleKey?: string | null,
): Promise<DeviceDto[]> {
  const page = await listDevicesPage({ tenantId, workspaceScope, limit: 200, offset: 0, moduleKey });
  return page.items;
}

export async function getDeviceById(
  deviceId: string,
  workspaceScope?: "all" | null,
  moduleKey?: string | null,
): Promise<DeviceDto> {
  const { data } = await apiClient.get<DeviceDto>(`/devices/${deviceId}`, {
    params: moduleKey ? { module_key: moduleKey } : undefined,
    __skipTenantHeader: workspaceScope === "all",
  });
  return data;
}

export async function createDevice(body: DeviceCreateDto): Promise<DeviceDto> {
  const { data } = await apiClient.post<DeviceDto>("/devices", body);
  return data;
}

export async function updateDevice(deviceId: string, body: DeviceUpdateDto): Promise<DeviceDto> {
  const { data } = await apiClient.patch<DeviceDto>(`/devices/${deviceId}`, body);
  return data;
}

export async function deleteDevice(deviceId: string): Promise<void> {
  await apiClient.delete(`/devices/${deviceId}`);
}

export type DeviceAccessSummaryDto = {
  assigned_count: number;
};

export async function getDeviceAccessSummary(): Promise<DeviceAccessSummaryDto> {
  const { data } = await apiClient.get<DeviceAccessSummaryDto>("/devices/access-summary");
  return data;
}

export async function updateDeviceAssignments(
  deviceId: string,
  userIds: string[],
  tenantId?: string | null,
): Promise<DeviceDto> {
  const { data } = await apiClient.put<DeviceDto>(`/devices/${deviceId}/assignments`, {
    user_ids: userIds,
    tenant_id: tenantId ?? null,
  });
  return data;
}

export async function listDeviceAssignedUserSuggestions(params: {
  tenantId?: string | null;
  workspaceScope?: "all" | null;
  q?: string | null;
  limit?: number;
  offset?: number;
}): Promise<DeviceAssignedUserSuggestionPageDto> {
  const query: Record<string, string | number> = {
    limit: Math.min(100, Math.max(1, params.limit ?? 20)),
    offset: Math.max(0, params.offset ?? 0),
  };
  if (params.tenantId) query.tenant_id = params.tenantId;
  if (params.workspaceScope === "all") query.workspace_scope = "all";
  if (params.q?.trim()) query.q = params.q.trim();
  const { data } = await apiClient.get<DeviceAssignedUserSuggestionPageDto>("/devices/assigned-user-suggestions", {
    params: query,
    __skipTenantHeader: params.workspaceScope === "all",
  });
  return data;
}

export async function getPublicDevice(token: string): Promise<PublicDeviceDto> {
  const { data } = await apiClient.get<PublicDeviceDto>(`/devices/public/${token}`);
  return data;
}

export type ListDeviceLocationSuggestionsParams = {
  tenantId?: string | null;
  workspaceScope?: "all" | null;
  q?: string | null;
  limit?: number;
  offset?: number;
  moduleKey?: string | null;
};

export async function listDeviceLocationSuggestions(
  params: ListDeviceLocationSuggestionsParams,
): Promise<DeviceLocationSuggestionPageDto> {
  const {
    tenantId,
    workspaceScope,
    q,
    limit = 20,
    offset = 0,
    moduleKey,
  } = params;
  const query: Record<string, string | number> = { limit, offset };
  if (tenantId) {
    query.tenant_id = tenantId;
  }
  if (workspaceScope === "all") {
    query.workspace_scope = "all";
  }
  if (q && q.trim()) {
    query.q = q.trim();
  }
  if (moduleKey) {
    query.module_key = moduleKey;
  }
  const { data } = await apiClient.get<DeviceLocationSuggestionPageDto>("/devices/location-suggestions", {
    params: query,
    __skipTenantHeader: workspaceScope === "all",
  });
  return data;
}

export async function getMasterViewSnapshot(deviceIds: string[]): Promise<MasterViewSnapshotPageDto> {
  const { data } = await apiClient.post<MasterViewSnapshotPageDto>("/devices/master-view/snapshot", {
    device_ids: deviceIds,
  });
  return data;
}

export async function listDeviceServiceRecords(deviceId: string): Promise<DeviceServiceRecordDto[]> {
  const { data } = await apiClient.get<DeviceServiceRecordDto[]>(`/devices/${deviceId}/service-records`);
  return data;
}

export async function createDeviceServiceRecord(
  deviceId: string,
  body: DeviceServiceRecordCreateDto,
): Promise<DeviceServiceRecordDto> {
  const { data } = await apiClient.post<DeviceServiceRecordDto>(`/devices/${deviceId}/service-records`, body);
  return data;
}

export async function updateDeviceServiceRecord(
  deviceId: string,
  recordId: string,
  body: DeviceServiceRecordUpdateDto,
): Promise<DeviceServiceRecordDto> {
  const { data } = await apiClient.put<DeviceServiceRecordDto>(`/devices/${deviceId}/service-records/${recordId}`, body);
  return data;
}

export async function listDeviceServiceRecordPlatformUsers(
  deviceId: string,
): Promise<DeviceServiceRecordPlatformUserPageDto> {
  const { data } = await apiClient.get<DeviceServiceRecordPlatformUserPageDto>(
    `/devices/${deviceId}/service-record-platform-users`,
  );
  return data;
}

export type DevicePhotoUploadDto = {
  path: string;
};

export async function uploadDevicePhoto(file: File): Promise<DevicePhotoUploadDto> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<DevicePhotoUploadDto>("/devices/photo-upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
}

export async function getDevicePhotoBlob(photoPath: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>("/devices/photo", {
    params: { path: photoPath },
    responseType: "blob",
  });
  return data;
}
